import { drainQueue, onKick } from './queue';
export { enqueue, hasQueuedItems, onConflict, onError, onBanner, discardPendingMutationsForBooking } from './queue';
export type { QueueItem, ConflictDetail } from './queue';

// Self-scheduling retry loop. While the queue has items we keep re-draining; the
// delay backs off after a failed pass (5s → ×2 → 5min cap) and resets to the
// base on a clean pass or an explicit kick (online / foreground / new enqueue).
const BASE_DELAY_MS = 5_000;
const MAX_DELAY_MS = 5 * 60 * 1_000;

let timer: ReturnType<typeof setTimeout> | null = null;
let delay = BASE_DELAY_MS;

function clearTimer(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

async function runDrain(): Promise<void> {
  clearTimer();
  const { remaining, lastFailed } = await drainQueue();
  if (remaining === 0) {
    delay = BASE_DELAY_MS; // nothing left — stop the loop, ready for the next kick
    return;
  }
  // Schedule the next attempt at the current delay, then grow it for the one
  // after (back off on failure; reset to base on a clean-but-not-empty pass).
  timer = setTimeout(() => { void runDrain(); }, delay);
  delay = lastFailed ? Math.min(delay * 2, MAX_DELAY_MS) : BASE_DELAY_MS;
}

/** Drain now and (re)start the retry loop at the base delay. */
export function kickDrain(): void {
  delay = BASE_DELAY_MS;
  void runDrain();
}

/**
 * Start listening to online/visibility events + new enqueues, and kick off an
 * initial drain. Called once at App mount.
 */
export function startSyncScheduler(): void {
  window.addEventListener('online', kickDrain);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') kickDrain();
  });
  onKick(kickDrain); // a freshly enqueued item restarts the loop
  kickDrain(); // drain any leftovers from a previous session
}
